import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Heart, ArrowRight, Phone, Shield, ChevronRight, CheckCircle2, AlertTriangle, MessageCircle, Loader2 } from 'lucide-react';
import { API } from '../utils/api';

const QUESTIONS = [
  { id: 'q1', text: '¿Consume más (o más seguido) de lo que la familia quisiera?' },
  { id: 'q2', text: '¿Ha intentado parar y no ha podido?' },
  { id: 'q3', text: '¿Ha mentido, escondido el consumo o conseguido dinero de forma irregular para consumir?' },
  { id: 'q4', text: '¿Ha descuidado responsabilidades (trabajo, estudios, hijos) por el consumo?' },
  { id: 'q5', text: '¿Hay episodios de violencia, riesgo o pérdida de control?' },
  { id: 'q6', text: '¿Reconoce que tiene un problema?', isReverse: true },
];

const REGIONES_CL = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', "O'Higgins", 'Maule', 'Ñuble', 'Biobío',
  'La Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes',
];

const SUSTANCIAS = ['Alcohol', 'Pasta base', 'Cocaína', 'Marihuana', 'Medicamentos', 'Múltiples', 'No sé'];

const FAQ_ITEMS = [
  { q: '¿Cómo sé si mi familiar es adicto o solo consume socialmente?', a: 'La diferencia clave es la pérdida de control. Si la persona no puede dejar de consumir aunque eso le esté generando problemas (familiares, laborales, de salud, económicos), si miente sobre el consumo o si necesita cada vez más para sentir el mismo efecto, ya no es uso recreativo. El test arriba te orienta de forma confidencial.' },
  { q: '¿Cómo ayudo a un familiar que no quiere reconocer su adicción?', a: 'La negación es uno de los síntomas más comunes. Evita confrontar bajo efecto del consumo. Habla en momentos de lucidez, desde tu preocupación y no desde el reproche. Considera una intervención familiar guiada por un profesional. Mientras tanto, cuídate tú: la codependencia agota.' },
  { q: '¿Se puede internar a una persona adicta sin su consentimiento en Chile?', a: 'En Chile la internación involuntaria existe pero está regulada: requiere riesgo vital inminente o trastorno mental grave certificado. Para casos sin riesgo inmediato, la mejor vía es lograr una motivación mínima a través de intervención profesional. Llama al 1412 (SENDA) para orientación gratuita.' },
  { q: '¿Cuánto cuesta internar a un familiar en un centro de rehabilitación?', a: 'Los centros residenciales en Chile cuestan entre $400.000 y $2.500.000 mensuales según el nivel (básico, intermedio, alto estándar). Los ambulatorios entre $80.000 y $500.000. Existen centros gratuitos vía SENDA/FONASA con lista de espera (2-8 semanas) y subvencionados con copago bajo.' },
  { q: '¿Existen centros gratuitos para mi familiar?', a: 'Sí. SENDA y FONASA financian centros gratuitos para beneficiarios tramos A y B. Llama al 1412 o pide hora en tu CESFAM/CECOSF. SinAdicciones.org también tiene un directorio de centros gratuitos por región actualizado.' },
  { q: '¿Qué hago si mi hijo/pareja se pone agresivo por el consumo?', a: 'Tu seguridad primero. Si hay violencia activa, llama al 133 (Carabineros) o 149 (Familia). Aleja a niños/as del entorno. No intentes razonar bajo efecto de sustancias. Considera medida de protección si hay riesgo recurrente. Llama al 1455 (orientación violencia intrafamiliar).' },
  { q: '¿Cuál es la diferencia entre residencial, ambulatorio y online?', a: 'Residencial: vive en el centro (28 a 180 días), ideal para casos graves o sin contención familiar. Ambulatorio: asiste 2-5 veces por semana, mantiene rutina laboral. Online: telemedicina, útil para mantenimiento o zonas alejadas. El test te sugiere cuál se ajusta a tu caso.' },
  { q: '¿Es confidencial? ¿Comparten mis datos?', a: 'Sí, 100% confidencial. Tus respuestas no se guardan asociadas a tu identidad. Si pides cotización, tus datos solo se comparten con los centros que coinciden con tu región y modalidad solicitada. Puedes pedir baja en cualquier momento.' },
];

const SITE = 'https://sinadicciones.org';
const PAGE_URL = `${SITE}/como-ayudar-a-un-familiar-con-adiccion`;

function calcScore(answers) {
  let score = 0;
  QUESTIONS.forEach((q) => {
    const v = Number(answers[q.id] || 0);
    score += q.isReverse ? (3 - v) : v;
  });
  // max = 18
  let nivel, modalidad, color, descripcion;
  if (score <= 4) {
    nivel = 'Bajo'; modalidad = 'ambulatorio'; color = 'text-emerald-300';
    descripcion = 'El consumo aún no parece estar fuera de control. Es momento ideal para prevenir y conversar.';
  } else if (score <= 9) {
    nivel = 'Moderado'; modalidad = 'ambulatorio'; color = 'text-amber-300';
    descripcion = 'Hay señales de problema. Una intervención temprana ambulatoria puede evitar que escale.';
  } else if (score <= 14) {
    nivel = 'Alto'; modalidad = 'residencial'; color = 'text-orange-300';
    descripcion = 'El consumo ya está afectando la vida. Recomendamos evaluación profesional pronto.';
  } else {
    nivel = 'Severo'; modalidad = 'residencial'; color = 'text-rose-300';
    descripcion = 'Hay riesgo elevado. Es urgente buscar un centro residencial o intervención profesional ya.';
  }
  return { nivel, modalidad, color, descripcion, score };
}

export default function FamiliarLandingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..6 (6 preguntas + 1 contexto)
  const [answers, setAnswers] = useState({});
  const [ctx, setCtx] = useState({ sustancia: '', vive: '', region: '', urgencia: '' });
  const [showResult, setShowResult] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const resultRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view_familiar', { page_path: '/como-ayudar-a-un-familiar-con-adiccion' });
    }
  }, []);

  const handleStartTest = () => {
    if (window.gtag) window.gtag('event', 'experiencia_familiar_iniciada', { source: 'familiar' });
    document.getElementById('test-anchor')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswer = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => setStep((s) => s + 1), 200);
  };

  const handleSubmitContext = () => {
    setShowResult(true);
    if (window.gtag) {
      window.gtag('event', 'experiencia_familiar_completada', { source: 'familiar', region: ctx.region, sustancia: ctx.sustancia });
    }
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleCotizar = () => {
    const result = calcScore(answers);
    if (window.gtag) {
      window.gtag('event', 'generate_lead', { source: 'familiar', modality: result.modalidad, region: ctx.region, value: 1 });
    }
    const params = new URLSearchParams({
      source: 'familiar',
      modalidad_sugerida: result.modalidad,
      region: ctx.region,
      urgencia: ctx.urgencia,
      sustancia: ctx.sustancia,
      target_type: 'center',
    });
    navigate(`/cotizar?${params.toString()}`);
  };

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { setEmailStatus('invalid'); return; }
    setEmailStatus('loading');
    try {
      await axios.post(`${API}/familiar/email-capture`, {
        email,
        region: ctx.region,
        sustancia: ctx.sustancia,
        urgencia: ctx.urgencia,
        nivel: calcScore(answers).nivel,
        modalidad_sugerida: calcScore(answers).modalidad,
        tag: 'familiar-lead',
      });
      setEmailStatus('ok');
      if (window.gtag) window.gtag('event', 'email_capture_familiar', { source: 'familiar' });
    } catch (err) {
      setEmailStatus('error');
    }
  };

  const result = showResult ? calcScore(answers) : null;
  const progress = Math.min(100, Math.round((step / 7) * 100));
  const allAnswered = QUESTIONS.every((q) => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* SEO (React 19 metadata hoisting — sin Helmet) */}
      <title>Cómo Ayudar a un Familiar con Adicción: Test y Guía (2026)</title>
      <meta name="description" content="¿Tu hijo, pareja o familiar consume drogas o alcohol? Haz el test gratis, descubre qué tan grave es, cómo hablarle y a qué centros postular en tu región. Confidencial." />
      <link rel="canonical" href={PAGE_URL} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Cómo Ayudar a un Familiar con Adicción: Test y Guía (2026)" />
      <meta property="og:description" content="Test confidencial de 7 preguntas + guía para hablarle + centros disponibles en tu región. Sin juzgar." />
      <meta property="og:url" content={PAGE_URL} />
      <meta property="og:image" content={`${SITE}/api/public/familiar/og-image`} />
      <meta property="og:image:secure_url" content={`${SITE}/api/public/familiar/og-image`} />
      <meta property="og:image:width" content="1280" />
      <meta property="og:image:height" content="896" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content="Madre acompañando a su hijo adulto en cocina iluminada — apoyo familiar en adicción" />
      <meta property="og:site_name" content="SinAdicciones.org" />
      <meta property="og:locale" content="es_CL" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Cómo Ayudar a un Familiar con Adicción: Test y Guía (2026)" />
      <meta name="twitter:description" content="Test confidencial de 7 preguntas + guía para hablarle + centros disponibles en tu región." />
      <meta name="twitter:image" content={`${SITE}/api/public/familiar/og-image`} />

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@type': 'MedicalWebPage',
        name: 'Cómo Ayudar a un Familiar con Adicción',
        url: PAGE_URL, inLanguage: 'es-CL',
        about: { '@type': 'MedicalCondition', name: 'Trastorno por uso de sustancias' },
        audience: { '@type': 'MedicalAudience', audienceType: 'Caregiver' },
        publisher: { '@type': 'Organization', name: 'SinAdicciones.org', url: SITE },
        lastReviewed: '2026-02-20',
      })}</script>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a }
        })),
      })}</script>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@type': 'HowTo',
        name: 'Cómo ayudar a un familiar con adicción a las drogas o alcohol en Chile',
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Identifica si es adicción', text: 'Aplica un test confidencial de 7 preguntas para evaluar la severidad del consumo.' },
          { '@type': 'HowToStep', position: 2, name: 'Cómo hablarle sin que se cierre', text: 'Acércate en momentos de lucidez, desde la preocupación. Evita acusaciones y ultimátums.' },
          { '@type': 'HowToStep', position: 3, name: 'Elige la modalidad correcta', text: 'Residencial para casos graves, ambulatorio para moderados, online para mantenimiento.' },
          { '@type': 'HowToStep', position: 4, name: 'Cotiza centros en tu región', text: 'Solicita propuestas a centros verificados de tu zona que coincidan con tu caso.' },
        ],
      })}</script>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Recursos para familias', item: `${SITE}/recursos` },
          { '@type': 'ListItem', position: 3, name: 'Cómo ayudar a un familiar con adicción', item: PAGE_URL },
        ],
      })}</script>

      <Header />

      {/* HERO */}
      <section className="relative px-4 pt-12 pb-10 sm:pt-20 sm:pb-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs mb-6">
            <Heart className="w-3.5 h-3.5" /> No estás solo. Pedir ayuda es un acto de valentía.
          </div>
          <h1 className="text-3xl sm:text-5xl font-semibold leading-tight mb-5">
            ¿Tu ser querido necesita ayuda con una adicción?
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mb-7 max-w-2xl mx-auto">
            Responde 7 preguntas y descubre qué tan grave es, cómo hablarle y a qué centros postular. Gratis y 100% confidencial.
          </p>
          <Button data-testid="cta-start-test" onClick={handleStartTest} size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-full">
            Empezar test gratis (30 seg) <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <div className="flex flex-wrap justify-center gap-3 mt-6 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />Gratis</span>
            <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" />Confidencial</span>
            <span className="inline-flex items-center gap-1.5"><Heart className="w-3.5 h-3.5 text-emerald-400" />Sin juzgar</span>
          </div>
        </div>
      </section>

      {/* TEST */}
      <section id="test-anchor" className="px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 sm:p-8">
              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-xs text-slate-400 mb-2">
                  <span>Pregunta {Math.min(step + 1, 7)} de 7</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
              </div>

              {step < 6 && (
                <div>
                  <h2 className="text-lg sm:text-xl font-medium mb-6">{QUESTIONS[step].text}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { v: 0, label: 'Nunca' },
                      { v: 1, label: 'A veces' },
                      { v: 2, label: 'Seguido' },
                      { v: 3, label: 'Siempre' },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        data-testid={`q${step + 1}-opt-${opt.v}`}
                        onClick={() => handleAnswer(QUESTIONS[step].id, opt.v)}
                        className={`py-3 px-4 rounded-lg border text-sm font-medium transition ${
                          answers[QUESTIONS[step].id] === opt.v
                            ? 'bg-emerald-500 border-emerald-500 text-slate-950'
                            : 'bg-slate-800 border-slate-700 hover:border-emerald-500 text-slate-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 6 && !showResult && (
                <div>
                  <h2 className="text-lg sm:text-xl font-medium mb-2">Cuéntanos tu contexto</h2>
                  <p className="text-sm text-slate-400 mb-6">Para sugerir centros y modalidad adecuada en tu región.</p>
                  <div className="space-y-3">
                    <select data-testid="ctx-sustancia" value={ctx.sustancia} onChange={(e) => setCtx({ ...ctx, sustancia: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm">
                      <option value="">¿Qué sustancia consume?</option>
                      {SUSTANCIAS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select data-testid="ctx-region" value={ctx.region} onChange={(e) => setCtx({ ...ctx, region: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm">
                      <option value="">Región</option>
                      {REGIONES_CL.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <select data-testid="ctx-vive" value={ctx.vive} onChange={(e) => setCtx({ ...ctx, vive: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm">
                      <option value="">¿Vive contigo?</option>
                      <option value="si">Sí, en la misma casa</option>
                      <option value="no">No, vive aparte</option>
                    </select>
                    <select data-testid="ctx-urgencia" value={ctx.urgencia} onChange={(e) => setCtx({ ...ctx, urgencia: e.target.value })} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm">
                      <option value="">¿Qué tan urgente?</option>
                      <option value="hoy">Necesito ayuda hoy</option>
                      <option value="esta-semana">Esta semana</option>
                      <option value="este-mes">Este mes</option>
                      <option value="explorando">Estoy explorando opciones</option>
                    </select>
                  </div>
                  <Button data-testid="ctx-submit" onClick={handleSubmitContext} disabled={!ctx.region || !ctx.sustancia || !ctx.urgencia} className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-3 disabled:opacity-50">
                    Ver mi resultado <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* RESULTADO */}
      {showResult && result && (
        <section ref={resultRef} className="px-4 py-10 bg-slate-900/50">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Nivel detectado</div>
                  <h2 className={`text-3xl sm:text-4xl font-bold ${result.color} mb-3`} data-testid="result-nivel">{result.nivel}</h2>
                  <p className="text-slate-300">{result.descripcion}</p>
                </div>

                <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-4 mb-6">
                  <div className="text-xs text-slate-400 mb-1">Modalidad recomendada</div>
                  <div className="text-lg font-semibold text-emerald-300 capitalize">{result.modalidad}</div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-400" />Cómo hablarle hoy</h3>
                    <p className="text-sm text-slate-400">&ldquo;Me importa lo que te está pasando y quiero entender cómo te sientes&rdquo; • &ldquo;No quiero juzgarte, solo necesito que sepas que estoy aquí.&rdquo;</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Qué evitar</h3>
                    <p className="text-sm text-slate-400">Confrontar bajo efecto del consumo • Amenazas o ultimátums • Esconder lo que pasa al resto de la familia.</p>
                  </div>
                </div>

                <Button data-testid="cta-cotizar" onClick={handleCotizar} size="lg" className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-6 text-base rounded-full">
                  Recibir propuestas de centros en {ctx.region} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>

                <div className="mt-6 pt-6 border-t border-slate-800">
                  <div className="text-xs text-slate-400 mb-3">📩 Envíame esta guía + lista de centros gratuitos por email</div>
                  {emailStatus === 'ok' ? (
                    <div className="text-sm text-emerald-300">¡Listo! Te llegará en minutos. Revisa también el spam.</div>
                  ) : (
                    <form onSubmit={handleEmailCapture} className="flex gap-2">
                      <Input data-testid="email-input" type="email" placeholder="tu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-slate-800 border-slate-700 text-slate-100" />
                      <Button data-testid="email-submit" type="submit" disabled={emailStatus === 'loading'} variant="outline" className="border-emerald-500 text-emerald-300 hover:bg-emerald-500/10">
                        {emailStatus === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Recibir'}
                      </Button>
                    </form>
                  )}
                  {emailStatus === 'invalid' && <div className="text-xs text-rose-400 mt-1">Email inválido</div>}
                  {emailStatus === 'error' && <div className="text-xs text-rose-400 mt-1">Algo falló, intenta de nuevo</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* GUÍA + FAQ + LINKS INTERNOS */}
      <section className="px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6">Cómo internar a un familiar o ayudarlo a aceptar tratamiento</h2>
          <ul className="space-y-3 text-slate-300 mb-10">
            <li>• Habla en momentos de lucidez, no bajo efecto del consumo.</li>
            <li>• Centra la conversación en cómo te hace sentir, no en lo que hace mal.</li>
            <li>• Ten información concreta lista: 1-2 centros, costo, modalidad.</li>
            <li>• Considera una intervención profesional si lleva meses sin aceptar.</li>
            <li>• Cuídate tú también. La codependencia agota. Busca apoyo (Al-Anon, Nar-Anon, terapia familiar).</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-6">Preguntas frecuentes</h2>
          <div className="space-y-2">
            {FAQ_ITEMS.map((f, i) => (
              <details key={i} className="bg-slate-900 border border-slate-800 rounded-lg group">
                <summary className="cursor-pointer p-4 font-medium text-slate-100 flex items-center justify-between">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition" />
                </summary>
                <div className="px-4 pb-4 text-sm text-slate-400">{f.a}</div>
              </details>
            ))}
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            <Link to="/centros-de-rehabilitacion-de-drogas-gratis" className="block p-5 rounded-lg bg-slate-900 border border-slate-800 hover:border-emerald-500 transition">
              <div className="text-xs text-emerald-300 mb-1">Recurso relacionado</div>
              <div className="font-medium">Centros gratuitos por región →</div>
            </Link>
            <Link to="/calculadora" className="block p-5 rounded-lg bg-slate-900 border border-slate-800 hover:border-emerald-500 transition">
              <div className="text-xs text-emerald-300 mb-1">Herramienta</div>
              <div className="font-medium">Calcula el costo del tratamiento →</div>
            </Link>
          </div>

          <div className="mt-10 p-5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
            <Phone className="w-5 h-5 inline-block mr-2 text-emerald-400" />
            <span className="text-slate-200">Crisis 24/7: </span>
            <a href="tel:1412" className="font-semibold text-emerald-300">Fonodrogas 1412</a>
            <span className="text-slate-400"> (SENDA, gratuito)</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
